import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-green) disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-(--color-green) text-(--color-on-accent) hover:opacity-90',
        outline: 'border border-(--color-border) bg-(--color-surface) hover:bg-(--color-bg) text-(--color-text2)',
        ghost: 'hover:bg-(--color-bg) text-(--color-text2)',
        danger: 'bg-(--color-red) text-(--color-on-accent) hover:opacity-90',
        secondary: 'bg-(--color-bg) text-(--color-text2) hover:bg-(--color-border)',
        toggle: 'border border-(--color-border) bg-(--color-surface) text-(--color-text2) hover:border-(--color-green) data-[on=true]:bg-(--color-selected) data-[on=true]:text-(--color-selected-fg) data-[on=true]:border-(--color-selected)',
      },
      size: {
        default: 'h-8 px-3',
        sm: 'h-7 px-2.5 text-[0.692em]',
        xs: 'h-6 px-2 text-[0.615em]',
        icon: 'h-8 w-8',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />;
  },
);
Button.displayName = 'Button';

export { buttonVariants };
